import { useQuery, useMutation } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { OpenAPI } from '../core/OpenAPI'
import { defer, set } from 'lodash-es'

/** 共通の進捗監視 hook */

interface progressMonitorOptions {
  processedAfterDisplayTime: number // 処理完了後にメッセージを表示する時間
  initialMessage: string // 初期メッセージ
  startMessage: string // 処理開始メッセージ
  endMessage: string // 処理完了メッセージ
}

const defaultOptions: progressMonitorOptions = {
  processedAfterDisplayTime: 2000,
  initialMessage: '処理を開始する準備をしています...',
  startMessage: '処理を開始',
  endMessage: '処理が完了しました',
}

const useProgressMonitor = (taskId: string | null, isActive: boolean,_options?: progressMonitorOptions) => {
    const [progress, setProgress] = useState<number | null>(null)
    const [lastLog, setLastLog] = useState<string>('')
    const [logs, setLogs] = useState<string[]>([])
    const [isObserving, setObserving] = useState<boolean>(false)
    const deferTimeout = useRef<number | null>(null)
    const eventSourceRef = useRef<EventSource | null>(null)
    // 失敗イベントを受け取った場合は完了メッセージで上書きしない
    const failedRef = useRef<boolean>(false)

    const options = { ...defaultOptions, ..._options }

    const connectEventSource = () => {
        console.debug('connectEventSource', taskId)
        if (eventSourceRef.current) {
            eventSourceRef.current.close()
            eventSourceRef.current = null
        }
        if (taskId) {
            const es = new EventSource(`/api/task/${taskId}/stream`)
            es.onmessage = (e) => {
                const data = JSON.parse(e.data)
                if (data.status === 'failed') {
                    failedRef.current = true
                }
                setProgress(data.progress)
                setLastLog(data.message)
                setLogs((prevLogs) => [...prevLogs, data.message])
            }
            es.onerror = (error) => {
                console.error('SSE error:', error)
            }
            eventSourceRef.current = es
        }
    }

    useEffect(() => {
        if (deferTimeout.current) {
          clearTimeout(deferTimeout.current)
          deferTimeout.current = null
        }
        if (isActive && taskId) {
          if(!eventSourceRef.current) {
            connectEventSource()
          }
          // 初期状態の設定
          failedRef.current = false
          setProgress(null)
          setLastLog(options.initialMessage)
          setLogs([])
          setObserving(true)
        } else if(taskId){
          // isActive -> false
          if(eventSourceRef.current) {
            eventSourceRef.current.close()
            eventSourceRef.current = null
          }
          if (!failedRef.current) {
            setProgress(1.0)
            setLastLog(options.endMessage)
            setLogs([])
          }
          // 完了メッセージを表示するために少し待つ
          deferTimeout.current = setTimeout(() => {
              setObserving(false)
          }, options.processedAfterDisplayTime) as unknown as number
        }else{
          // タスクがない場合はクリーンアップ
          if (eventSourceRef.current) {
              eventSourceRef.current.close()
              eventSourceRef.current = null
          }
          setProgress(0)
          setLastLog(options.initialMessage)
          setLogs([])
          setObserving(false)
        }
        // クリーンアップ処理
        return () => {
            if (eventSourceRef.current) {
                eventSourceRef.current.close()
                eventSourceRef.current = null
            }
            if (deferTimeout.current) {
              clearTimeout(deferTimeout.current)
              deferTimeout.current = null
            }
        }
    }, [isActive])

    return { progress, lastLog, logs, isObserving, eventSource: eventSourceRef.current }
}

export interface TaskQueryOptions {
    enabled?: boolean
    retry?: boolean | number
    cache?: boolean
}

/**
 * Query 用の hook
 *
 * 使用例:
 * const { data, isLoading, isError, error, refetch, progress, lastLog, logs, isObserving } = useTaskWithProgress<HelloResponse>(
 *   HelloService.helloApiHelloGet,
 *  { shouldError: false, waitTime: 10 },
 *  {
 *    enabled: true,
 *   retry: false,
 *  }
 * )
 *
 */
export const useTaskWithProgress = <T>(
    apiCall: () => Promise<T>,
    key?: unknown,
    options?: TaskQueryOptions
) => {
  const [taskId,setTaskId] = useState<string | null>(null)
  const [isActive,setIsActive] = useState<boolean>(false)

    const query = useQuery<T, Error>({
        queryKey: [key ?? 'task'],
        queryFn: () => {
            // タスク実行直前に taskId を生成してヘッダーに設定
            const newTaskId = uuidv4()
            setTaskId(newTaskId)
            OpenAPI.HEADERS = {
                'X-Task-Id': newTaskId,
            }
            // すぐにisActiveをtrueにするとstreamが間に合わない時があるので、少し待つ
            setTimeout(()=>setIsActive(true),100)
            return apiCall().then((data) => {
              // タスク完了
              console.debug("apiCall", data)
              setIsActive(false)
              return data
          }).catch((error) => {
              // 失敗時も監視を終了してSSE接続を閉じる
              setIsActive(false)
              throw error
          })
        },
        enabled: options?.enabled ?? true,
        retry: options?.retry ?? false,
        refetchOnWindowFocus: false,
        ...(options?.cache === false ? {
          cacheTime: 0,
          staleTime: 0,
          gcTime: 0,
        } : {})
      })

    const { progress, lastLog, logs, isObserving, eventSource } = useProgressMonitor(
        taskId,
        isActive
    )

    return {
        ...query,
        taskId,
        eventSource,
        progress,
        lastLog,
        logs,
        isObserving,
    }
}

export interface TaskMutationOptions {
    onSuccess?: (data: unknown) => void
    onError?: (error: Error) => void
}

/**
 * Mutation 用の hook (useMutationTaskWithProgress)
 *
 * 使用例:
 * const { data, isLoading, isError, error, mutateAsync, progress, lastLog, logs, isObserving } = useMutationTaskWithProgress<HelloResponse, [boolean,number]>(
 *   HelloService.helloApiHelloGet,
 *   {
 *    onSuccess: (data) => {
 *     console.log("Success", data)
 *    },
 *   onError: (error) => {
 *    console.error("Error", error)
 *   },
 *  }
 * )
 *
 * 更新時:
 *
 * mutateAsync([false, 10])
 *
 */
export const useMutationTaskWithProgress = <T, Args extends unknown[]>(
    apiCall: (...args: Args) => Promise<T>,
    options?: TaskMutationOptions
) => {
    const [taskId,setTaskId] = useState<string | null>(null)
    const [isActive,setIsActive] = useState<boolean>(false)

    const mutation = useMutation<T, Error, Args>({
      mutationFn: async (args: Args) => {
        const newTaskId = uuidv4()
        setTaskId(newTaskId)
        OpenAPI.HEADERS = { 'X-Task-Id': newTaskId }
        // すぐにisActiveをtrueにするとstreamが間に合わない時があるので、少し待つ
        setTimeout(() => setIsActive(true), 100)
        try {
          const data = await apiCall(...args)
          console.debug('apiCall', data)
          setIsActive(false)
          options?.onSuccess?.(data)
          return data
        } catch (error) {
          setIsActive(false)
          if (error instanceof Error) {
            options?.onError?.(error)
          }
          throw error
        }
      },
    })

    const { progress, lastLog, logs, isObserving, eventSource } = useProgressMonitor(
        taskId,
        isActive
    )

    return {
        ...mutation,
        taskId,
        eventSource,
        progress,
        lastLog,
        logs,
        isObserving,
    }
}
